import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import profile_api from '../../api-services/profile/profile.services';
import GlobalLayout from '../../layouts/GlobalLayout';
import LazyLoad from 'react-lazyload';
import { getFirstLetters, tinyAlert } from '../../app-utils';
import TopBar from '../../layouts/~includes/TopBar';
import Preloader from '../../components/preloader/Preloader';
import { FaStar } from 'react-icons/fa';
import { createReview } from '../../api-services/ratings/useCreateUpdateReview';

// main-component-ui
const CreateReview = () => {
  document.body.classList.remove('overflow-hidden');

  const navigate = useNavigate();
  const { userID } = useParams();

  // global state localstorage
  const [me, setMe] = useState<any>({});
  const [pageLoading, setPageLoading] = useState<boolean>(false);
  
  // review states
  const [rating, setRating] = useState<number>(0);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [description, setDescription] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);

  // use side effects
  useEffect(() => {
    myProfile(userID ?? '');
  }, [userID]);

  const myProfile = async (userID: string) => {
    setPageLoading(true);
    try {
      const my_profile: any = await profile_api.ProfileById(userID);
      setMe(my_profile.data.data);
    } catch (error) {
      console.error('Error loading profile:', error);
      tinyAlert('Error loading user profile');
    } finally {
      setPageLoading(false);
    }
  };

  const handleRatingClick = (value: number) => {
    setRating(value);
  };

  const handleSubmitReview = async () => {
    if (rating === 0) {
      tinyAlert('Please select a rating');
      return;
    }

    setSubmitting(true);
    try {
      const response = await createReview({
        reviewed_user_id: Number(me.id), 
        rating: rating, 
        comment: description 
      });
      
      if (response.status === true) {
        tinyAlert('success', 'Review submitted successfully!');
        // Navigate back after success
        setTimeout(() => {
          navigate(`/me/${me.username}?activeTab=reviews`);
        }, 1500);
      } else {
        throw new Error(response.message || 'Failed to submit review');
      }
    } catch (error: any) {
      console.error('Error submitting review:', error);
      tinyAlert(error.message || 'Error submitting review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <GlobalLayout title="Catapot | Create Review">
      <section className="min-h-screen flex flex-col dark:bg-neutral-800">
        {/* top lead area and post btn */}
        <div className="fixed top-0 w-full z-50">
          <TopBar title="Write a Review" goBack={true} />
        </div>

        {/* content profile-info area */}
        {pageLoading ? (
          <Preloader loading={pageLoading} />
        ) : (
          <div className="mt-[80px] flex-1 flex flex-col dark:bg-neutral-800">
            {/* User Profile Section */}
            <div className="bg-white dark:bg-neutral-800 flex gap-2 justify-start py-3 px-3 pb-3 border-b border-gray-100">
              {/* profile image side */}
              <div className="overflow-hidden flex justify-center">
                <div className="relative">
                  {me?.profile_picture_url ? (
                    <LazyLoad height={300} offset={1000} once>
                      <img
                        alt="me"
                        src={me?.profile_picture_url}
                        className="border-[1px] object-cover rounded-full w-[50px] h-[50px] bg-gray-200 shadow-sm overflow-hidden"
                      />
                    </LazyLoad>
                  ) : (
                    <span className="flex justify-center items-center w-[50px] h-[50px] bg-custom-600 rounded-full border border-green-100">
                      <h3 className="text-white text-3xl uppercase font-[600]">
                        {getFirstLetters(me?.first_name ?? 'C')}
                      </h3>
                    </span>
                  )}
                </div>
              </div>
              <div className='flex-1'>
                <div className='text-sm font-bold'>
                  {me ? me.first_name : 'First Name'} {me ? me.last_name : 'Last Name'}
                </div>
                <div className='text-gray-400 text-xs mt-1'>
                  Reviews are public and include your account information
                </div>
              </div>
            </div>

            {/* Rating Section */}
            <div className="px-4 py-6">
              <div className="flex gap-2 justify-between items-center px-12">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    className="focus:outline-none transform transition-transform hover:scale-110"
                    onClick={() => handleRatingClick(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                  >
                    <FaStar
                      size={28}
                      className={`${
                        (hoverRating || rating) >= star
                          ? 'text-yellow-400'
                          : 'text-gray-300'
                      } transition-colors duration-200`}
                    />
                  </button>
                ))}
              </div>
              {rating > 0 && (
                <p className="text-center mt-3 text-sm text-gray-600">
                  You rated: {rating} {rating === 1 ? 'star' : 'stars'}
                </p>
              )}
            </div>

            {/* Review Description Section */}
            <div className="px-4 flex-1 flex flex-col mt-2">
              <div className="relative flex-1">
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Share your experience... (optional)"
                  className="w-full min-h-[120px] dark:bg-inherit p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-custom-600 focus:border-transparent resize-y text-sm"
                  maxLength={500}
                />
              </div>
              
              {/* Character count */}
              {description.length > 0 && (
                <p className="text-xs text-gray-400 mt-2 text-right">
                  {description.length}/500 characters
                </p>
              )}
            </div>

            {/* Submit Button at Bottom */}
            <div className="px-4 py-6 mt-auto">
              <button
                onClick={handleSubmitReview}
                disabled={submitting || rating === 0}
                className={`w-full py-3 rounded-lg font-semibold transition-all duration-200 ${
                  rating > 0
                    ? 'bg-custom-600 text-white hover:bg-custom-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {submitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>
          </div>
        )}
      </section>
    </GlobalLayout>
  );
};

export default CreateReview;